import { registerToolDefinition, type ToolCall, type ToolResult } from '@edge/edge-ai'
import {
  ANALYSIS_ARTIFACTS,
  ANALYSIS_OPERATIONS,
  type AnalyzeBacktestRequest,
  type AnalysisSelection,
} from '@/features/backtest/analysis/analysis-contract'
import { runBacktestAnalysis } from '@/features/backtest/analysis/run-analysis'
import {
  getActiveGraphApi,
  subscribeActiveGraphApi,
} from '@/features/backtest/graph-api/active-context'
import {
  subscribeAutomation,
  type AutomationTurn,
} from '@/features/backtest/hooks/graph-automation'
import { useAccountStore } from '@/stores/account-store'
import type { ActionCallbacks } from './types'
import { getHeadlessExecutionContext } from './headless-execution-context'

registerToolDefinition({
  name: 'analyze_backtest',
  description:
    'Read-only bounded Parquet analysis for the current owned execution. Never runs a backtest. Missing or historical artifacts return unavailable. Use schema to discover physical columns, then sample or summarize. No URLs or code accepted.',
  input_schema: {
    type: 'object',
    properties: {
      executionId: { type: 'string' },
      artifact: { type: 'string', enum: [...ANALYSIS_ARTIFACTS] },
      operation: { type: 'string', enum: [...ANALYSIS_OPERATIONS] },
      selection: {
        type: 'object',
        properties: {
          columns: { type: 'array', items: { type: 'string' } },
          rowStart: { type: 'integer', minimum: 0 },
          rowEnd: { type: 'integer', minimum: 1 },
          limit: { type: 'integer', minimum: 1, maximum: 200 },
          symbol: { type: 'string' },
          timeframe: { type: 'string' },
          startTime: { type: 'string' },
          endTime: { type: 'string' },
          signalColumn: { type: 'string' },
          fillTimestampColumn: { type: 'string' },
          beforeMs: { type: 'number', enum: [0] },
          afterMs: { type: 'number', minimum: 0 },
          timezone: { type: 'string', enum: ['UTC'] },
        },
        additionalProperties: false,
      },
    },
    required: ['executionId', 'artifact', 'operation'],
    additionalProperties: false,
  },
})

function requestFromInput(input: Record<string, unknown>): AnalyzeBacktestRequest {
  if (
    Object.keys(input).some(
      (key) => !['executionId', 'artifact', 'operation', 'selection'].includes(key),
    )
  )
    throw new Error('Unknown analysis argument.')
  if (typeof input.executionId !== 'string' || !input.executionId || input.executionId.length > 256)
    throw new Error('executionId is required.')
  if (
    !ANALYSIS_ARTIFACTS.includes(input.artifact as never) ||
    !ANALYSIS_OPERATIONS.includes(input.operation as never)
  )
    throw new Error('Unknown artifact or operation.')
  const selection = input.selection
  if (selection !== undefined) {
    if (!selection || typeof selection !== 'object' || Array.isArray(selection))
      throw new Error('Invalid selection.')
    const fields = selection as Record<string, unknown>
    for (const [key, value] of Object.entries(fields)) {
      if (key === 'columns') {
        if (
          !Array.isArray(value) ||
          value.length > 200 ||
          value.some((item) => typeof item !== 'string' || item.length > 256)
        )
          throw new Error('Invalid columns.')
      } else if (['rowStart', 'rowEnd', 'limit', 'beforeMs', 'afterMs'].includes(key)) {
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0)
          throw new Error(`Invalid ${key}.`)
      } else if (
        [
          'symbol',
          'timeframe',
          'startTime',
          'endTime',
          'signalColumn',
          'fillTimestampColumn',
          'timezone',
        ].includes(key)
      ) {
        if (typeof value !== 'string' || value.length > 256) throw new Error(`Invalid ${key}.`)
      } else throw new Error('Unknown selection argument.')
    }
  }
  return {
    executionId: input.executionId,
    artifact: input.artifact as AnalyzeBacktestRequest['artifact'],
    operation: input.operation as AnalyzeBacktestRequest['operation'],
    selection: selection as AnalysisSelection | undefined,
  }
}

export async function executeAnalyzeBacktest(
  call: ToolCall,
  callbacks: ActionCallbacks,
  turn?: AutomationTurn,
): Promise<ToolResult> {
  const fail = (message: string): ToolResult => ({
    tool_use_id: call.tool_use_id,
    success: false,
    is_error: true,
    content: message,
  })
  let request: AnalyzeBacktestRequest
  try {
    request = requestFromInput(call.input)
  } catch (error) {
    return fail(error instanceof Error ? error.message : 'Invalid request.')
  }
  const api = getActiveGraphApi()
  const accountId = useAccountStore.getState().session?.user?.sub
  const editorResult = callbacks.getBacktestResults?.()
  const editorMatches =
    !!api &&
    editorResult?.execution_id === request.executionId &&
    api.getRunResult()?.executionId === request.executionId
  const headless = getHeadlessExecutionContext(turn?.id, accountId, request.executionId)
  if (!accountId || (!editorMatches && !headless)) {
    return fail(
      'Execution unavailable: only the current owned run can be analyzed. Historical artifact lookup is not supported here.',
    )
  }
  const result = editorMatches ? editorResult! : headless!.result
  const url =
    request.artifact === 'transactions' ? result.transaction_file_url : result.debug_file_url
  if (typeof url !== 'string' || !url)
    return fail('Artifact unavailable: this execution did not capture the requested data.')
  request.strategyId = editorMatches ? api!.getStrategyInfo().id : headless!.strategyId
  const controller = new AbortController()
  const cancel = () => controller.abort()
  if (turn?.signal.aborted) cancel()
  turn?.signal.addEventListener('abort', cancel, { once: true })
  const unsubscribe = subscribeAutomation(cancel)
  const unsubscribeEditor = editorMatches
    ? subscribeActiveGraphApi(() => {
        if (getActiveGraphApi() !== api) cancel()
      })
    : () => undefined
  const unsubscribeAccount = useAccountStore.subscribe((state) => {
    if (state.session?.user?.sub !== accountId) cancel()
  })
  try {
    const transactionUrl = result.transaction_file_url
    const response = await runBacktestAnalysis(
      request,
      {
        executionId: request.executionId,
        strategyId: request.strategyId,
        artifact: request.artifact,
        url,
        ...(request.operation === 'signal_window'
          ? {
              related: {
                executionId: request.executionId,
                strategyId: request.strategyId,
                artifact: 'transactions' as const,
                ...(typeof transactionUrl === 'string' ? { url: transactionUrl } : {}),
              },
            }
          : {}),
      },
      { signal: controller.signal },
    )
    if (
      controller.signal.aborted ||
      (editorMatches
        ? getActiveGraphApi() !== api || api!.getRunResult()?.executionId !== request.executionId
        : getHeadlessExecutionContext(turn?.id, accountId, request.executionId) !== headless)
    )
      return fail('Analysis cancelled: the active execution changed.')
    if (response.status === 'error' && response.code === 'decode_failed')
      response.message = 'The owned artifact could not be fetched or decoded.'
    return {
      tool_use_id: call.tool_use_id,
      success: response.ok,
      is_error: !response.ok,
      content: JSON.stringify(response),
    }
  } catch {
    // Transport errors can contain private artifact locations. Do not echo them to the model.
    return fail('Artifact analysis failed or was cancelled. No backtest was dispatched.')
  } finally {
    unsubscribe()
    unsubscribeAccount()
    unsubscribeEditor()
    turn?.signal.removeEventListener('abort', cancel)
  }
}
